import { some, map, sumBy, uniq, find, cloneDeep, minBy, maxBy } from 'lodash';
import moment from 'moment';
import ImageURL from '../../helpers/ImageURL';
import {dateRange, dateRangeFlat, formattedDateRange} from '../../helpers/DateRange';
import { formatChartLabel, formatChartLabelItem, sortDateRange } from '../../helpers/DateFormatter';
import { percentageValue } from "../../helpers/NumberFormatter";

export const spotifyDiscoveryFormatter = {
	formatSpotifyDiscovery,
	formatSpotifyDiscoveryFlat,
	formatSpotifyDiscoveryTotals,
	formatSpotifyDiscoveryTimeseries,
	formatSpotifyDiscoveryMainTimeseries,
	formatSpotifyDiscoveryDetailsTimeseries,
	formatMetadata
};



function formatSpotifyDiscovery(data){
	const dates = uniq(map(data, 'current_end_date')).sort();
	const tracks = uniq(map(data, 'track_id'));
	let rows = [];
	let timeseries = {};
	let grandTotalStreams = 0, grandTotalMonetized = 0, grandTotalUnmonetized = 0;
	for(let date of dates) {
		timeseries[date] = {monetized: 0, unmonetized: 0};
	}
	
	for(let track of tracks) {
		let lastMonthStreams = 0, lastMonthListeners = 0, lastMonthStatus = '',
			totalStreams = 0, totalMonetized = 0, totalUnmonetized = 0,
			monthsUnmonetized = 0, unmonetizedSince = '';
		
		for(let date of dates) {
			let row = find(data, item=>(item.current_end_date == date && item.track_id == track));
			if(row) {
				lastMonthStreams = row.current_months_plays;
				lastMonthListeners = row.current_months_unique_users;
				lastMonthStatus = row.status;
				grandTotalStreams += row.current_months_plays;
				
				totalStreams += row.current_months_plays;
				if(row.status.endsWith('Unmonetized')) {
					totalUnmonetized += row.current_months_plays;
					grandTotalUnmonetized += row.current_months_plays;
					timeseries[date].unmonetized+=row.current_months_plays;
					monthsUnmonetized++;
					if(!unmonetizedSince)
						unmonetizedSince = row.current_end_date;
				}
				else {
					totalMonetized += row.current_months_plays;
					grandTotalMonetized += row.current_months_plays;
					timeseries[date].monetized+=row.current_months_plays;
					unmonetizedSince = '';
				}
			}
		}
		rows.push({id: track, lastMonthStreams, lastMonthListeners, lastMonthStatus, totalStreams, totalMonetized, totalUnmonetized, monthsUnmonetized, unmonetizedSince})
	};
	const monetizedTracks = rows.filter(row=>!row.lastMonthStatus.endsWith('Unmonetized')).length;
	const unmonetizedTracks = rows.filter(row=>row.lastMonthStatus.endsWith('Unmonetized')).length;
	const metrics = [
		{id: 'total_streams', title: 'Streams', value: grandTotalStreams},
		{id: 'monetized_streams', title: 'Monetized Streams', value: grandTotalMonetized, percentageValue: Math.round(grandTotalMonetized / grandTotalStreams * 100), valueClass: 'up'},
		{id: 'unmonetized_streams', title: 'Unmonetized Streams', value: grandTotalUnmonetized, percentageValue: Math.round(grandTotalUnmonetized / grandTotalStreams * 100), valueClass: 'down'},
		{id: 'monetized_tracks', title: 'Monetized Tracks', value: monetizedTracks, percentageValue: Math.round(monetizedTracks / rows.length * 100), valueClass: 'up'},
		{id: 'unmonetized_tracks', title: 'Unmonetized Tracks', value: unmonetizedTracks, percentageValue: Math.round(unmonetizedTracks / rows.length * 100), valueClass: 'down'}
	];
	
	let monetizedDataset = [], unmonetizedDataset = [];
	for(let date of dates) {
		let column = timeseries[date];
		monetizedDataset.push(column.monetized);
		unmonetizedDataset.push(column.unmonetized);
	};
	timeseries = {
		labels: dates,
		datasets: [
			{label: "Monetized", data: monetizedDataset},
			{label: "Unmonetized", data: unmonetizedDataset},
		]
	}

	return {data: rows, metrics, timeseries};
}

function formatSpotifyDiscoveryFlat(data) {
	return data;
}

function formatMetadata(data, metadata) {
	const tracks = cloneDeep(data);
	let rows = [];
	for(let track of tracks) {
		let trackMeta = find(metadata, meta=>meta.id == track.track_id);
		if(trackMeta) {
			track.title = trackMeta.title;
			track.isrc = trackMeta.isrc;
			track.release_date = trackMeta.release_date;
			track.artist = trackMeta.primary_artist.name;
			track.logo = ImageURL(trackMeta.product_image, trackMeta.product_id, 'tracks');
		}
		rows.push(track);
	}
	tracks.data = rows;
	return tracks;
}

function formatSpotifyDiscoveryTotals(data) {
	data = data[0];
	let metrics = [
		{id: 'monthly_radio_units', title: 'Last Month\'s Radio Streams', value: data.curr_month_radio_units, percentageValue: percentageValue(data.curr_month_radio_units, data.prev_month_radio_units) },
		{id: 'monthly_collection_units', title: 'Last Month\'s Collection Streams', value: data.curr_month_collection_units, percentageValue: percentageValue(data.curr_month_collection_units, data.prev_month_collection_units)},
		{id: 'radio_units', title: 'Last Year\'s Radio Streams', value: data.curr_year_radio_units, percentageValue: percentageValue(data.curr_year_radio_units, data.prev_year_radio_units)},
		{id: 'collection_units', title: 'Last Year\'s Collection Streams', value: data.curr_year_collection_units, percentageValue: percentageValue(data.curr_year_radio_units, data.prev_year_radio_units)}
	];
	
	metrics = metrics.map(metric=>{
		metric.valueClass = (metric.percentageValue > 0) ? 'up' : 'down'
		return metric;
	});

	return metrics;
}

function formatSpotifyDiscoveryTimeseries(data) {
	let labels = [],
		datasets = [];
	
	
	if(data && data.length) {
		const {dates, period} = dateRange(data);
		datasets = data.map((src, index)=>{
			src.stms_by_date = src.stms_by_date.sort(sortDateRange);
			let data = [];
			for(let date of dates) {
				let stms = find(src.stms_by_date, {stream_date: date});
				data.push(stms ? stms.curr_units : null);
			}    
	
			return {
				id: src.source,
				label: src.source,
				data,
				fill: false,
				//borderColor: colors[index%colors.length],
				//backgroundColor: colors[index%colors.length]            
			}
		});    
		
		
		labels =  map(dates, (date)=>formatChartLabelItem(date, period));
	
	}
	return { labels, datasets };
}

function formatSpotifyDiscoveryMainTimeseries(data, metadata) {
	const sourceTypes = uniq(map(data, 'source'));
	let result = {};
	for(let source of sourceTypes) {
		const sourceData = data.filter(item=>item.source == source);
		
		let labels = [],
			datasets = [];
		
		const {dates, period} = dateRange(sourceData);
		datasets = sourceData.map((src, index)=>{
			src.stms_by_date = src.stms_by_date.sort(sortDateRange);
			let dataset = [];
			for(let date of dates) {
				let stms = find(src.stms_by_date, {stream_date: date});
				dataset.push(stms ? stms.curr_units : null);
			}    
			const trackMeta = find(metadata, meta=>meta.id == src.track_id);
	
			return {
				id: src.track_id,
				label: trackMeta ? trackMeta.title : 'N/A',
				data: dataset,
				fill: false,
				//borderColor: colors[index%colors.length],
				//backgroundColor: colors[index%colors.length]            
			}
		});    
		
		
		labels =  map(dates, (date)=>formatChartLabelItem(date, period));
	
	

		result[source] = { labels, datasets };
	}
	return result;
}

function formatSpotifyDiscoveryDetailsTimeseries(data) {
	let labels = [],
		datasets = [],
		baseline = 0,
		baselineCount = 0,
		totals = {},
		minRadio = 0,
		maxRadio = 0,
		basedOnCompletePeriod = false,
		average = 0,
		metrics = [];
	
	if(data && data.length) {
		const prevData = data.map(entry=>{
			const newEntry = {
				...entry, 
				stms_by_date: entry.stms_by_date.filter(row => row.period_group == 'prev')
			}
			return newEntry;
		});
		
		data = data.map(entry=>{
			const newEntry = {
				...entry, 
				stms_by_date: entry.stms_by_date.filter(row => row.period_group == 'curr')
			}
			return newEntry;
		});

		
		const {dates, period} = dateRange(data);
		datasets = data.map((src, index)=>{
			src.stms_by_date = src.stms_by_date.sort(sortDateRange);
			let data = [];
			for(let date of dates) {
				let stms = find(src.stms_by_date, {stream_date: date});
				data.push(stms ? stms.curr_units : null);
			}    
	
			const total = sumBy(src.stms_by_date, 'curr_units');
			totals[src.source] = total;
			average += Math.round(total / src.stms_by_date.length);
			
			if(src.source == 'Radio') {
				let fullStatements = src.stms_by_date.filter(st=>st.is_complete_period);
				basedOnCompletePeriod = true;
				if(!fullStatements.length) {
					fullStatements = src.stms_by_date;
					basedOnCompletePeriod = false;
				}
					
				const minFullStatements = minBy(fullStatements, 'curr_units');
				const maxFullStatements = maxBy(fullStatements, 'curr_units');
				if(minFullStatements)
					minRadio = minFullStatements.curr_units;
				if(maxFullStatements)	
					maxRadio = maxFullStatements.curr_units;
				
			}
			
			return {
				id: src.source,
				label: src.source,
				data,
				fill: false,
				//borderColor: colors[index%colors.length],
				//backgroundColor: colors[index%colors.length]            
			}

		});    
		
		for(let src of prevData) {
			const baselineEntries = src.stms_by_date;
			baseline += sumBy(baselineEntries, 'curr_units');
			baselineCount += baselineEntries.length;
		}

		
		if(baselineCount)
			baseline = Math.round(baseline / baselineCount);
		
		const breakeven = Math.round(baseline * 1/0.7);	
		metrics = [
			{id: 'total_radio_streams', title: 'Total Radio Streams', value: totals['Radio']},
			{id: 'total_collection_streams', title: 'Total Collection Streams', value: totals['Collection']},
			{id: 'baseline', title: 'Average Pre-Entry Streams', value: baseline},
			{id: 'breakeven', title: 'Breakeven Streams', value: breakeven},	
			{id: 'min_radio', title: 'Min Radio Streams', value: minRadio, basedOnCompletePeriod},
			{id: 'max_radio', title: 'Max Radio Streams', value: maxRadio, basedOnCompletePeriod},
		];
	
			
		
		
		labels =  map(dates, (date)=>formatChartLabelItem(date, period));
	
	}
	return { labels, datasets, baseline, metrics };

}